import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { api, API_BASE, uploadImage } from '../../api/client';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Divider from '@mui/material/Divider';
import AdminLoading from './AdminLoading';
import BlockRenderer from '../../Components/BlockRenderer/BlockRenderer';
import { MdDragIndicator } from 'react-icons/md';

const BLOCK_TYPES = [
  { type: 'hero', label: 'Hero slider' },
  { type: 'how_it_works', label: 'How it works' },
  { type: 'hot_list', label: 'Hot list (4 blocks)' },
  { type: 'category_grid', label: 'Category grid' },
  { type: 'instagram', label: 'Instagram' },
  { type: 'newsletter', label: 'Newsletter' },
  { type: 'text', label: 'Text' },
  { type: 'image', label: 'Image' },
  { type: 'video', label: 'Video' },
  { type: 'button', label: 'Button' },
];

function ImagePicker({ value, onChange, label = 'Image' }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = React.useRef(null);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const url = await uploadImage(file);
      onChange(url);
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          fullWidth
          label={label}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste URL or click Upload"
          error={!!error}
          helperText={error}
          sx={{ flex: 1, minWidth: 180 }}
        />
        <Button component="label" variant="outlined" size="small" disabled={uploading} sx={{ flexShrink: 0 }}>
          {uploading ? '…' : 'Upload'}
          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" hidden onChange={handleFile} />
        </Button>
      </Box>
      {value && (
        <Box component="img" src={value.startsWith('http') ? value : API_BASE + value} alt="" sx={{ mt: 1, maxHeight: 72, maxWidth: 140, objectFit: 'contain', border: 1, borderColor: 'divider', borderRadius: 1 }} />
      )}
    </Box>
  );
}

function SortableSeasonRow({ id, label, imageUrl, title, onImageChange, onTitleChange, titlePlaceholder }) {
  const sortableId = `season-${id}`;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortableId });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  return (
    <Paper ref={setNodeRef} style={style} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ cursor: 'grab', touchAction: 'none', display: 'flex', alignItems: 'center', pt: 0.5 }} {...attributes} {...listeners}>
          <MdDragIndicator size={20} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{label}</Typography>
          <ImagePicker value={imageUrl || ''} onChange={onImageChange} label="Background image" />
          <TextField size="small" fullWidth label={`${label} title`} value={title || ''} onChange={(e) => onTitleChange(e.target.value)} placeholder={titlePlaceholder} />
        </Box>
      </Box>
    </Paper>
  );
}

function NewSeasonBlockEditor({ block, onUpdate }) {
  const p = block?.props || {};
  const blockOrder = Array.isArray(p.blockOrder) && p.blockOrder.length === 2
    ? p.blockOrder
    : ['women', 'men'];
  const innerSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const handleInnerDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = blockOrder.indexOf(String(active.id).replace('season-', ''));
    const newIndex = blockOrder.indexOf(String(over.id).replace('season-', ''));
    if (oldIndex === -1 || newIndex === -1) return;
    onUpdate({ blockOrder: arrayMove([...blockOrder], oldIndex, newIndex) });
  };
  const seasonLabels = { women: 'Women block', men: 'Men block' };
  const seasonPlaceholders = { women: "Women's Rentals", men: "Men's Spring Rentals 2026" };
  const getImage = (k) => (k === 'women' ? p.womenImageUrl : p.menImageUrl);
  const getTitle = (k) => (k === 'women' ? p.womenTitle : p.menTitle);
  const setImage = (k, url) => onUpdate(k === 'women' ? { womenImageUrl: url } : { menImageUrl: url });
  const setTitle = (k, v) => onUpdate(k === 'women' ? { womenTitle: v } : { menTitle: v });
  return (
    <Box>
      <TextField size="small" fullWidth label="Section title" value={p.title || ''} onChange={(e) => onUpdate({ title: e.target.value })} placeholder="New Season – Rent the Trends" sx={{ mb: 2 }} />
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Drag to swap Women / Men order</Typography>
      <DndContext sensors={innerSensors} collisionDetection={closestCenter} onDragEnd={handleInnerDragEnd}>
        <SortableContext items={blockOrder.map((k) => `season-${k}`)} strategy={verticalListSortingStrategy}>
          {blockOrder.map((key) => (
            <SortableSeasonRow
              key={key}
              id={key}
              label={seasonLabels[key]}
              imageUrl={getImage(key)}
              title={getTitle(key)}
              titlePlaceholder={seasonPlaceholders[key]}
              onImageChange={(url) => setImage(key, url)}
              onTitleChange={(v) => setTitle(key, v)}
            />
          ))}
        </SortableContext>
      </DndContext>
    </Box>
  );
}

function SortableBlockItem({ block, index, isSelected, onSelect, onRemove, disabled }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id, disabled });

  const label = BLOCK_TYPES.find((b) => b.type === block.type)?.label || block.type;
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    marginBottom: 8,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <li ref={setNodeRef} style={style}>
      <Paper
        variant="outlined"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.5,
          bgcolor: isSelected ? 'action.selected' : 'background.paper',
          borderColor: isSelected ? 'primary.main' : 'divider',
          borderWidth: isSelected ? 2 : 1,
        }}
      >
        <span
          {...attributes}
          {...listeners}
          style={{ cursor: disabled ? 'default' : 'grab', touchAction: 'none', display: 'flex', color: 'text.secondary' }}
          aria-label="Drag to reorder"
        >
          <MdDragIndicator size={20} />
        </span>
        <Button
          fullWidth
          size="small"
          onClick={() => onSelect(index)}
          sx={{ justifyContent: 'flex-start', textTransform: 'none', color: 'text.primary' }}
        >
          {label}
        </Button>
        <IconButton size="small" onClick={() => onRemove(index)} aria-label="Remove block" sx={{ color: 'text.secondary' }}>×</IconButton>
      </Paper>
    </li>
  );
}

/** Migrate legacy new_season blocks to hot_list so the 4-section editor is used and all data is stored. */
function migrateNewSeasonToHotList(content) {
  if (!Array.isArray(content)) return content;
  return content.map((block) => {
    if (block.type !== 'new_season') return block;
    const p = block.props || {};
    return {
      ...block,
      type: 'hot_list',
      id: block.id ? String(block.id).replace('new_season', 'hot_list') : 'hot_list-' + Date.now(),
      props: {
        block1: { tag: 'HOT LIST', title: p.womenTitle || 'WOMEN COLLECTION', link: '/shop?gender=female', ...(p.womenImageUrl && { imageUrl: p.womenImageUrl }) },
        block2: { tag: 'HOT LIST', title: p.menTitle || 'MEN COLLECTION', link: '/shop?gender=male', ...(p.menImageUrl && { imageUrl: p.menImageUrl }) },
        block3: { tag: 'HOT LIST', title: 'KIDS COLLECTION', link: '/shop' },
        block4: { title: 'E-GIFT CARDS', subtitle: 'Surprise someone with the gift they really want.', link: '/shop', backgroundColor: '#f5d5c8' },
      },
    };
  });
}

export default function AdminPageEditor() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [content, setContent] = useState([]);
  const [title, setTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [layout, setLayout] = useState('default');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    if (!slug) return;
    api(`/api/admin/pages/${encodeURIComponent(slug)}`)
      .then((data) => {
        setPage(data);
        setTitle(data.title || '');
        setMetaDescription(data.meta_description || '');
        setLayout(data.layout || 'default');
        const raw = Array.isArray(data.content) ? data.content : [];
        setContent(migrateNewSeasonToHotList(raw));
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleSave = async () => {
    setError('');
    setSaving(true);
    try {
      await api(`/api/admin/pages/${encodeURIComponent(slug)}`, {
        method: 'PUT',
        body: JSON.stringify({ title, meta_description: metaDescription, layout, content })
      });
      setSaving(false);
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  const addBlock = (type) => {
    const id = type + '-' + Date.now();
    let props = {};
    if (type === 'hot_list') {
      props = {
        block1: { tag: 'HOT LIST', title: 'WOMEN COLLECTION', link: '/shop?gender=female' },
        block2: { tag: 'HOT LIST', title: 'MEN COLLECTION', link: '/shop?gender=male' },
        block3: { tag: 'HOT LIST', title: 'KIDS COLLECTION', link: '/shop' },
        block4: { title: 'E-GIFT CARDS', subtitle: 'Surprise someone with the gift they really want.', link: '/shop', backgroundColor: '#f5d5c8' },
      };
    } else if (type === 'video') {
      props = { title: 'Watch our rental experience', url: '', poster: '', caption: '' };
    }
    const block = { type, id, props };
    setContent((prev) => [...prev, block]);
    setSelectedIndex(content.length);
  };

  const removeBlock = (index) => {
    setContent((prev) => prev.filter((_, i) => i !== index));
    if (selectedIndex === index) setSelectedIndex(null);
    else if (selectedIndex > index) setSelectedIndex(selectedIndex - 1);
  };

  const updateBlockProps = (index, newProps) => {
    setContent((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], props: { ...(next[index].props || {}), ...newProps } };
      return next;
    });
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over || active.id === over.id) return;
    const oldIndex = content.findIndex((b) => b.id === active.id);
    const newIndex = content.findIndex((b) => b.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(content, oldIndex, newIndex);
    setContent(reordered);
    if (selectedIndex === oldIndex) setSelectedIndex(newIndex);
    else if (selectedIndex !== null) {
      if (selectedIndex === newIndex) setSelectedIndex(newIndex);
      else if (oldIndex < selectedIndex && newIndex >= selectedIndex) setSelectedIndex(selectedIndex - 1);
      else if (oldIndex > selectedIndex && newIndex <= selectedIndex) setSelectedIndex(selectedIndex + 1);
    }
  };

  const selectedBlock = selectedIndex != null ? content[selectedIndex] : null;
  const blockIds = content.map((b) => b.id);

  if (loading) return <AdminLoading />;
  if (error && !page) return <Typography color="error" className="admin-error">{error}</Typography>;

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Edit page: {slug}</Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>Page settings</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <TextField size="small" label="Page title" value={title} onChange={(e) => setTitle(e.target.value)} sx={{ width: 220 }} />
          <TextField size="small" label="Meta description" value={metaDescription} onChange={(e) => setMetaDescription(e.target.value)} sx={{ width: 300 }} />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Layout</InputLabel>
            <Select value={layout} label="Layout" onChange={(e) => setLayout(e.target.value)}>
              <MenuItem value="default">Default</MenuItem>
              <MenuItem value="fullwidth">Full width</MenuItem>
              <MenuItem value="boxed">Boxed</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save page'}</Button>
          <Button variant="outlined" onClick={() => window.open(slug === 'home' ? '/' : `/${slug}`, '_blank')}>Open preview</Button>
        </Box>
        {error && <Typography color="error" variant="body2" sx={{ mt: 1 }}>{error}</Typography>}
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
        <Paper variant="outlined" sx={{ p: 2, flex: '0 0 260px' }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>Page blocks</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>Drag to reorder · Click to edit</Typography>
          <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <SortableContext items={blockIds} strategy={verticalListSortingStrategy}>
                  {content.map((block, i) => (
                    <SortableBlockItem
                      key={block.id}
                      block={block}
                      index={i}
                      isSelected={selectedIndex === i}
                      onSelect={setSelectedIndex}
                      onRemove={removeBlock}
                      disabled={content.length <= 1}
                    />
                  ))}
                </SortableContext>
              </ul>
              <DragOverlay>
                {activeId ? (
                  <Box sx={{ bgcolor: 'background.paper', boxShadow: 2, px: 1.5, py: 1, borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MdDragIndicator size={20} />
                    <Typography variant="body2">{content.find((b) => b.id === activeId)?.type ?? activeId}</Typography>
                  </Box>
                ) : null}
              </DragOverlay>
            </DndContext>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Add block</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {BLOCK_TYPES.map((b) => (
                <Button key={b.type} size="small" variant="outlined" onClick={() => addBlock(b.type)} sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>+ {b.label}</Button>
              ))}
            </Box>
          </Paper>

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, gap: 2 }}>
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
              <Box sx={{ px: 1.5, py: 1, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle2" color="text.secondary">Preview</Typography>
              </Box>
              <Box
                sx={{
                  maxHeight: 380,
                  overflow: 'auto',
                  bgcolor: 'background.default',
                  '& [data-block-index]': { outline: '2px solid transparent', outlineOffset: 2 },
                  '& [data-block-index].selected': { outlineColor: 'primary.main' },
                }}
              >
                {content.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>Add blocks from the left. Click a block to edit.</Typography>
                ) : (
                  <BlockRenderer
                    content={content}
                    onBlockClick={setSelectedIndex}
                    selectedBlockIndex={selectedIndex}
                  />
                )}
              </Box>
            </Paper>
            {selectedBlock && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                  Edit: {BLOCK_TYPES.find((b) => b.type === selectedBlock.type)?.label || selectedBlock.type}
                </Typography>
                <PropsEditor block={selectedBlock} onUpdate={(props) => updateBlockProps(selectedIndex, props)} />
              </Paper>
            )}
          </Box>
        </Box>
      </Box>
  );
}

function PropsEditor({ block, onUpdate }) {
  const p = block.props || {};
  if (block.type === 'hero') {
    const slides = Array.isArray(p.slides) ? p.slides : [];
    const setSlides = (next) => onUpdate({ slides: next });
    const updateSlide = (index, field, val) => {
      const next = slides.map((s, i) => (i === index ? { ...s, [field]: val } : s));
      setSlides(next);
    };
    const addSlide = () => setSlides([...slides, { label: '', title: '', cta: '', ctaLink: '/shop', imageUrl: '', alt: '' }]);
    const removeSlide = (index) => setSlides(slides.filter((_, i) => i !== index));
    return (
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>{slides.length} slide(s)</Typography>
        {slides.map((slide, index) => (
          <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle2">Slide {index + 1}</Typography>
              <Button size="small" color="error" onClick={() => removeSlide(index)}>Remove</Button>
            </Box>
            <TextField size="small" fullWidth label="Label" value={slide.label || ''} onChange={(e) => updateSlide(index, 'label', e.target.value)} sx={{ mb: 1.5, display: 'block' }} />
            <TextField size="small" fullWidth label="Title" value={slide.title || ''} onChange={(e) => updateSlide(index, 'title', e.target.value)} sx={{ mb: 1.5, display: 'block' }} />
            <TextField size="small" fullWidth label="CTA text" value={slide.cta || ''} onChange={(e) => updateSlide(index, 'cta', e.target.value)} sx={{ mb: 1.5, display: 'block' }} />
            <TextField size="small" fullWidth label="CTA link" value={slide.ctaLink || ''} onChange={(e) => updateSlide(index, 'ctaLink', e.target.value)} sx={{ mb: 1.5, display: 'block' }} />
            <ImagePicker value={slide.imageUrl || ''} onChange={(url) => updateSlide(index, 'imageUrl', url)} label="Image" />
            <TextField size="small" fullWidth label="Alt text" value={slide.alt || ''} onChange={(e) => updateSlide(index, 'alt', e.target.value)} />
          </Paper>
        ))}
        <Button variant="outlined" fullWidth onClick={addSlide}>+ Add slide</Button>
      </Box>
    );
  }
  if (block.type === 'how_it_works') {
    return (
      <Box>
        <TextField size="small" fullWidth label="Section title" value={p.title || ''} onChange={(e) => onUpdate({ title: e.target.value })} sx={{ mb: 1.5 }} />
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Steps (JSON: array of {`{ "title", "description" }`})</Typography>
        <TextField size="small" fullWidth multiline rows={5} value={JSON.stringify(p.steps || [], null, 2)} onChange={(e) => { try { onUpdate({ steps: JSON.parse(e.target.value || '[]') }); } catch (_) {} }} sx={{ fontFamily: 'monospace' }} />
      </Box>
    );
  }
  if (block.type === 'newsletter') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <TextField size="small" fullWidth label="Title" value={p.title || ''} onChange={(e) => onUpdate({ title: e.target.value })} />
        <TextField size="small" fullWidth label="Subtitle" value={p.subtitle || ''} onChange={(e) => onUpdate({ subtitle: e.target.value })} />
        <TextField size="small" fullWidth label="Placeholder" value={p.placeholder || ''} onChange={(e) => onUpdate({ placeholder: e.target.value })} />
        <TextField size="small" fullWidth label="Button text" value={p.buttonText || ''} onChange={(e) => onUpdate({ buttonText: e.target.value })} />
      </Box>
    );
  }
  if (block.type === 'text') {
    return (
      <TextField size="small" fullWidth multiline rows={4} label="HTML content" value={p.html || ''} onChange={(e) => onUpdate({ html: e.target.value })} />
    );
  }
  if (block.type === 'image') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <ImagePicker value={p.url || ''} onChange={(url) => onUpdate({ url })} label="Image" />
        <TextField size="small" fullWidth label="Alt text" value={p.alt || ''} onChange={(e) => onUpdate({ alt: e.target.value })} />
        <TextField size="small" fullWidth label="Link (optional)" value={p.link || ''} onChange={(e) => onUpdate({ link: e.target.value })} />
      </Box>
    );
  }
  if (block.type === 'video') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <TextField size="small" fullWidth label="Title" value={p.title || ''} onChange={(e) => onUpdate({ title: e.target.value })} />
        <TextField size="small" fullWidth label="Video URL" value={p.url || ''} onChange={(e) => onUpdate({ url: e.target.value })} placeholder="/uploads/video.mp4 or YouTube embed URL" />
        <ImagePicker value={p.poster || ''} onChange={(poster) => onUpdate({ poster })} label="Poster image" />
        <TextField size="small" fullWidth label="Caption" value={p.caption || ''} onChange={(e) => onUpdate({ caption: e.target.value })} />
      </Box>
    );
  }
  if (block.type === 'instagram') {
    const images = Array.isArray(p.images) ? p.images : [];
    const setImages = (next) => onUpdate({ images: next });
    const updateImage = (index, url) => {
      const next = [...images];
      next[index] = typeof images[index] === 'object' && images[index] !== null ? { ...images[index], url } : url;
      setImages(next);
    };
    const updateImageMeta = (index, field, raw) => {
      const next = [...images];
      const cur = next[index];
      const url =
        typeof cur === 'string' ? cur || '' : typeof cur?.url === 'string' ? cur.url : '';
      next[index] = { ...(typeof cur === 'object' && cur !== null ? cur : {}), url, [field]: raw };
      setImages(next);
    };
    const getUrl = (item) => (typeof item === 'string' ? item : item?.url || '');
    const getMeta = (item, field) =>
      typeof item === 'object' && item !== null && typeof item[field] === 'string' ? item[field] : '';
    const addImage = () => setImages([...images, '']);
    const removeImage = (index) => setImages(images.filter((_, i) => i !== index));
    return (
      <Box>
        <TextField size="small" fullWidth label="Section title" value={p.title || ''} onChange={(e) => onUpdate({ title: e.target.value })} placeholder="@gemfashionstudio" sx={{ mb: 2 }} />
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Add post images below for a storefront-only grid (no Elfsight, no quotas). Omit images to rely on Elfsight when enabled.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>{images.length} image(s)</Typography>
        {images.map((urlOrObj, index) => (
          <Paper key={index} variant="outlined" sx={{ p: 1.5, mb: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <ImagePicker value={getUrl(urlOrObj)} onChange={(url) => updateImage(index, url)} label={`Image ${index + 1}`} />
              <TextField
                size="small"
                fullWidth
                label="Alt text (SEO)"
                value={getMeta(urlOrObj, 'alt')}
                onChange={(e) => updateImageMeta(index, 'alt', e.target.value)}
                sx={{ mt: 1 }}
              />
              <TextField
                size="small"
                fullWidth
                label="Link when tapped (optional — default opens Instagram)"
                value={getMeta(urlOrObj, 'link')}
                onChange={(e) => updateImageMeta(index, 'link', e.target.value)}
                placeholder="https://"
                sx={{ mt: 1 }}
              />
            </Box>
            <IconButton size="small" onClick={() => removeImage(index)} aria-label="Remove" sx={{ mt: 0.5 }}>×</IconButton>
          </Paper>
        ))}
        <Button variant="outlined" fullWidth onClick={addImage}>+ Add image</Button>
      </Box>
    );
  }
  if (block.type === 'new_season') {
    return <NewSeasonBlockEditor block={block} onUpdate={onUpdate} />;
  }
  if (block.type === 'hot_list') {
    const labels = ['Block 1 (left, large)', 'Block 2 (right top)', 'Block 3 (right bottom left)', 'Block 4 (right bottom right)'];
    return (
      <Box>
        {[1, 2, 3, 4].map((n) => {
          const key = `block${n}`;
          const b = p[key] || {};
          const updateBlock = (updates) => onUpdate({ [key]: { ...b, ...updates } });
          return (
            <Paper key={key} variant="outlined" sx={{ p: 1.5, mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>{labels[n - 1]}</Typography>
              <TextField size="small" fullWidth label="Tag (e.g. HOT LIST)" value={b.tag || ''} onChange={(e) => updateBlock({ tag: e.target.value })} sx={{ mb: 1 }} />
              <TextField size="small" fullWidth label="Title" value={b.title || ''} onChange={(e) => updateBlock({ title: e.target.value })} sx={{ mb: 1 }} />
              <TextField size="small" fullWidth label="Subtitle (optional)" value={b.subtitle || ''} onChange={(e) => updateBlock({ subtitle: e.target.value })} sx={{ mb: 1 }} />
              <ImagePicker value={b.imageUrl || ''} onChange={(url) => updateBlock({ imageUrl: url })} label="Background image" />
              <TextField size="small" fullWidth label="Background color (if no image)" value={b.backgroundColor || ''} onChange={(e) => updateBlock({ backgroundColor: e.target.value })} placeholder="#f5f0eb" sx={{ mt: 1 }} />
              <TextField size="small" fullWidth label="Link" value={b.link || ''} onChange={(e) => updateBlock({ link: e.target.value })} placeholder="/shop" sx={{ mt: 1 }} />
            </Paper>
          );
        })}
      </Box>
    );
  }
  if (block.type === 'button') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <TextField size="small" fullWidth label="Button text" value={p.text || ''} onChange={(e) => onUpdate({ text: e.target.value })} />
        <TextField size="small" fullWidth label="Link" value={p.link || ''} onChange={(e) => onUpdate({ link: e.target.value })} />
      </Box>
    );
  }
  return <Typography variant="body2" color="text.secondary">No editor for this block. Edit via API if needed.</Typography>;
}
